import { HubConnection } from "@microsoft/signalr/dist/esm/HubConnection";
import {
  CallDetails,
  TelephonyProviderState,
  CallStatus,
  TelephonyEvent,
  CallDirection,
} from "../models";

import { NormalizedPhone, normalizePhoneNumber } from "./utils/phoneUtils";

export class TelephonyBaseProvider {
  constructor(connection?: HubConnection) {
    this.signalRconnection = connection;
    // The SignalR connection is established asynchronously (useSignalR), so it can
    // still be null when a provider is constructed. Guard the subscription so the
    // constructor never throws "Cannot read properties of null (reading 'on')".
    this.signalRconnection?.on("TelephonyEvents", this.boundHandleSignalrEvs);
  }
  protected signalRconnection: HubConnection; // SignalR connection, if needed

  // Bound once so release() can pass the same reference to off() — a fresh
  // bind(this) would not match the callback registered with on().
  private readonly boundHandleSignalrEvs = this.handleSignalrEvs.bind(this);

  protected state: TelephonyProviderState = {
    isLoggedIn: false,
    isAvailable: false,
    status: CallStatus.Idle,
  };
  protected currentCall: CallDetails | null = null;

  protected httpHeaders?: any;

  private handleSignalrEvs(events: [TelephonyEvent]): void {
    console.log("Processing Base SignalR events:", events);

    events.forEach((event: TelephonyEvent) => {
      console.log("handing event:", event);
    });
  }

  async dial(number: string): Promise<void> {
    try {
      console.log("Dialing number:", number);
      // Implement the actual dialing logic here
    } catch (error) {
      console.error("Error dialing number:", error);
    }
  }

  protected normalizePhoneForDial(
    phoneNumber: string,
    defaultCallingCode: string = "+27" // numeric calling code
  ): NormalizedPhone {
    // Pass the numeric calling code directly to normalizePhoneNumber
    const normalized = normalizePhoneNumber(phoneNumber, defaultCallingCode);
    return normalized;
  }

  protected createCallDetails(
    normalizedNumber: NormalizedPhone,
    direction: CallDirection,
    contactId?: string
  ): CallDetails {
    return {
      id: contactId || new Date().toISOString(),
      callerNumber: normalizedNumber.original,
      callerName: direction === CallDirection.Inbound ? "Unknown" : undefined,
      direction: direction,
      startTime: new Date(),
      numberDetails: normalizedNumber,
    };
  }

  protected release(): void {
    // No-op when the provider was constructed before SignalR was available.
    this.signalRconnection?.off("TelephonyEvents", this.boundHandleSignalrEvs);
  }

  getCallDetails(callId?: string): CallDetails | null {
    if (!callId && this.currentCall) {
      return this.currentCall;
    }
    const rawId = this.currentCall.id.split("-")[0];
    if (callId && this.currentCall && rawId === callId) {
      return this.currentCall;
    }

    return null;
  }

  getState(): TelephonyProviderState {
    return { ...this.state };
  }

  // Event registration methods
  protected onIncomingCallRingingCallbacks: ((details: CallDetails) => void)[] =
    [];
  protected onIncomingCallAnsweredCallbacks: ((
    details: CallDetails
  ) => void)[] = [];
  protected onOutboundCallInitiatedCallbacks: ((
    details: CallDetails
  ) => void)[] = [];
  protected onOutboundCallAnsweredCallbacks: ((
    details: CallDetails
  ) => void)[] = [];
  protected onCallTransferredCallbacks: ((
    details: CallDetails,
    phoneNumber: string
  ) => void)[] = [];
  protected onCallEndedCallbacks: ((details: CallDetails) => void)[] = [];
  protected onIncomingCallRejectedCallbacks: ((
    details: CallDetails
  ) => void)[] = [];
  protected onOutgoingCallRejectedCallbacks: ((
    details: CallDetails
  ) => void)[] = [];
  protected onAvailabilityChangedCallbacks: ((isAvailable: boolean) => void)[] =
    [];
  protected onLoggedInChangedCallbacks: ((isLoggedIn: boolean) => void)[] = [];
  protected onStatusChangedCallbacks: ((status: CallStatus) => void)[] = [];

  onIncomingCallRinging(callback: (details: CallDetails) => void): void {
    this.onIncomingCallRingingCallbacks.push(callback);
  }

  onIncomingCallAnswered(callback: (details: CallDetails) => void): void {
    this.onIncomingCallAnsweredCallbacks.push(callback);
  }

  onOutboundCallInitiated(callback: (details: CallDetails) => void): void {
    this.onOutboundCallInitiatedCallbacks.push(callback);
  }

  onOutboundCallAnswered(callback: (details: CallDetails) => void): void {
    this.onOutboundCallAnsweredCallbacks.push(callback);
  }

  onCallTransferred(
    callback: (details: CallDetails, targetAgentId: string) => void
  ): void {
    this.onCallTransferredCallbacks.push(callback);
  }

  onCallEnded(callback: (details: CallDetails) => void): void {
    this.onCallEndedCallbacks.push(callback);
  }

  onIncomingCallRejected(callback: (details: CallDetails) => void): void {
    this.onIncomingCallRejectedCallbacks.push(callback);
  }

  onOutgoingCallRejected(callback: (details: CallDetails) => void): void {
    this.onOutgoingCallRejectedCallbacks.push(callback);
  }

  onAvailabilityChanged(callback: (isAvailable: boolean) => void): void {
    this.onAvailabilityChangedCallbacks.push(callback);
  }

  onLoggedInChanged(callback: (isLoggedIn: boolean) => void): void {
    this.onLoggedInChangedCallbacks.push(callback);
  }

  onStatusChanged(callback: (status: CallStatus) => void): void {
    this.onStatusChangedCallbacks.push(callback);
  }
}
